import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from "aws-amplify/auth";
import { apiUrl } from "./config.js";
import "./config.js";

const AuthContext = createContext(null);

const TOTP_APP_NAME = "Freight Train of Justice";

/**
 * Map Amplify signIn / confirmSignIn nextStep → challenge state for SignIn UI.
 * @returns {{ ok?: true, challenge?: string, totpSetup?: { sharedSecret: string, setupUri: string } }}
 */
function mapSignInStep(result) {
  const step = result.nextStep?.signInStep;
  if (result.isSignedIn || step === "DONE") {
    return { ok: true };
  }
  if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
    return { challenge: "NEW_PASSWORD_REQUIRED" };
  }
  if (step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
    return { challenge: "TOTP_CODE" };
  }
  if (step === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
    const details = result.nextStep?.totpSetupDetails;
    const setupUri = details?.getSetupUri?.(TOTP_APP_NAME);
    return {
      challenge: "TOTP_SETUP",
      totpSetup: {
        sharedSecret: details?.sharedSecret || "",
        setupUri: setupUri?.href || setupUri?.toString?.() || String(setupUri || ""),
      },
    };
  }
  if (step === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION") {
    // Prefer TOTP when Cognito offers a choice.
    return { challenge: "MFA_SELECT", allowed: result.nextStep?.allowedMFATypes || [] };
  }
  throw new Error(`Sign-in incomplete (${step || "unknown"}). Contact an admin.`);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null);
  const [groups, setGroups] = useState([]);

  const refreshGroups = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const access = session.tokens?.accessToken;
      const payload = access?.payload;
      const g = payload?.["cognito:groups"];
      setGroups(Array.isArray(g) ? g.map(String) : []);
    } catch {
      setGroups([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      setUser(current);
      setChallenge(null);
      setTotpSetup(null);
      await refreshGroups();
      return current;
    } catch {
      setUser(null);
      setGroups([]);
      return null;
    }
  }, [refreshGroups]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const applyStep = useCallback(
    async (mapped) => {
      if (mapped.ok) {
        await refresh();
        return { ok: true };
      }
      setChallenge(mapped.challenge);
      if (mapped.totpSetup) setTotpSetup(mapped.totpSetup);
      else if (mapped.challenge !== "TOTP_SETUP") setTotpSetup(null);
      return mapped;
    },
    [refresh]
  );

  const login = useCallback(
    async (email, password) => {
      const result = await signIn({
        username: email.trim().toLowerCase(),
        password,
      });
      return applyStep(mapSignInStep(result));
    },
    [applyStep]
  );

  const completeNewPassword = useCallback(
    async (newPassword) => {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      return applyStep(mapSignInStep(result));
    },
    [applyStep]
  );

  /** Complete TOTP code challenge or finish TOTP setup during sign-in. */
  const completeTotpChallenge = useCallback(
    async (code) => {
      const result = await confirmSignIn({
        challengeResponse: String(code).trim(),
      });
      return applyStep(mapSignInStep(result));
    },
    [applyStep]
  );

  /** When Cognito asks MFA type, pick TOTP. */
  const selectTotpMfa = useCallback(async () => {
    const result = await confirmSignIn({ challengeResponse: "TOTP" });
    return applyStep(mapSignInStep(result));
  }, [applyStep]);

  /** Cognito emails a code to the user. No admin approval. */
  const requestPasswordReset = useCallback(async (email) => {
    const username = email.trim().toLowerCase();
    await resetPassword({ username });
    return { ok: true };
  }, []);

  /**
   * Confirm Cognito code + new password, then notify admin (best-effort).
   * Does not grant leads-access.
   */
  const confirmPasswordReset = useCallback(async (email, code, newPassword) => {
    const username = email.trim().toLowerCase();
    await confirmResetPassword({
      username,
      confirmationCode: String(code).trim(),
      newPassword,
    });

    if (apiUrl) {
      try {
        await fetch(`${apiUrl}/auth/password-reset-notify`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: username }),
        });
      } catch {
        // Reset already succeeded; admin email is best-effort.
      }
    }

    return { ok: true };
  }, []);

  /** Optional MFA enrollment while already signed in. */
  const beginOptionalTotpSetup = useCallback(async () => {
    const details = await setUpTOTP();
    const setupUri = details.getSetupUri(TOTP_APP_NAME);
    return {
      sharedSecret: details.sharedSecret || "",
      setupUri: setupUri?.href || setupUri?.toString?.() || String(setupUri || ""),
    };
  }, []);

  const confirmOptionalTotpSetup = useCallback(async (code) => {
    await verifyTOTPSetup({ code: String(code).trim() });
    await updateMFAPreference({ totp: "PREFERRED" });
    return { ok: true };
  }, []);

  const getMfaStatus = useCallback(async () => {
    try {
      return await fetchMFAPreference();
    } catch {
      return null;
    }
  }, []);

  const disableOptionalTotp = useCallback(async () => {
    await updateMFAPreference({ totp: "DISABLED" });
    return { ok: true };
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    setUser(null);
    setGroups([]);
    setChallenge(null);
    setTotpSetup(null);
  }, []);

  /** ID token for API Gateway Cognito JWT authorizer (`aud` = app client id). */
  const getIdToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error("No session token");
    return token;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      challenge,
      totpSetup,
      groups,
      isAdmin: groups.includes("admin"),
      login,
      completeNewPassword,
      completeTotpChallenge,
      selectTotpMfa,
      requestPasswordReset,
      confirmPasswordReset,
      beginOptionalTotpSetup,
      confirmOptionalTotpSetup,
      getMfaStatus,
      disableOptionalTotp,
      logout,
      getIdToken,
      isAuthenticated: Boolean(user),
    }),
    [
      user,
      loading,
      challenge,
      totpSetup,
      groups,
      login,
      completeNewPassword,
      completeTotpChallenge,
      selectTotpMfa,
      requestPasswordReset,
      confirmPasswordReset,
      beginOptionalTotpSetup,
      confirmOptionalTotpSetup,
      getMfaStatus,
      disableOptionalTotp,
      logout,
      getIdToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
